import { ResponseError } from '@ais-ui/api-spec/shapes';
import * as Sentry from '@sentry/browser';
import { requestRateMonitor } from '@ais-ui/utils';
import { useServiceAvailability } from './ServiceAvailabilityProvider';

/**
 * Maps URL path segments to logical service names for availability tracking.
 * When a 404 (not found/deployed) or 401 (caller unauthorized) is received on
 * one of these service paths, the service is marked as unavailable
 * (circuit-breaker pattern).
 *
 * Matching is done in order — the first match wins, so more specific paths
 * (e.g. `/shiptracker/nextport/`) must come before general ones.
 */
export const SERVICE_PATH_MAP: Record<string, string> = {
  '/portserver/': 'portserver',
  '/shiptracker/nextport/': 'nextport',
  '/shipdb/': 'shipdb',
  '/config/shapes/': 'shapes',
  '/config/shiplist': 'shiplist',
  '/eventdetector/': 'eventdetector',
  '/ingress/grounding/': 'grounding',
};

/**
 * Error thrown when a request is skipped because the target service
 * has been marked unavailable by the circuit-breaker.
 *
 * The originating HTTP `status` (401/404) is preserved so downstream
 * classification (`@ais-ui/custom-hooks` `classifyApiError`) and telemetry
 * can still tell "caller unauthorized for this service" apart from "service
 * not deployed".
 */
export class ServiceUnavailableError extends Error {
  public readonly serviceName: string;
  public readonly status?: number;

  constructor(serviceName: string, status?: number) {
    super(`Service "${serviceName}" is currently unavailable`);
    this.name = 'ServiceUnavailableError';
    this.serviceName = serviceName;
    this.status = status;
  }
}

/**
 * Resolve the logical service name for a given URL by matching against
 * SERVICE_PATH_MAP entries. Returns undefined when no match is found.
 */
export function resolveServiceName(
  url: string | undefined,
): string | undefined {
  if (!url) return undefined;
  for (const [pathSegment, serviceName] of Object.entries(SERVICE_PATH_MAP)) {
    if (url.includes(pathSegment)) return serviceName;
  }
  return undefined;
}

/**
 * Transient gateway/proxy status codes. These indicate the upstream backend
 * was momentarily unreachable or slow — not an application bug. React Query
 * retries them, so logging every occurrence at `error` level floods Sentry
 * with non-actionable noise (issue 2F5, a 504 Gateway Time-out). `500`/`501`
 * are intentionally excluded as they usually indicate a real server fault.
 */
const TRANSIENT_SERVER_STATUSES = new Set([502, 503, 504]);

export function isTransientServerError(status: number | undefined): boolean {
  return status !== undefined && TRANSIENT_SERVER_STATUSES.has(status);
}

// Interface for service availability callbacks
interface ServiceAvailabilityCallbacks {
  isAvailable: (serviceName: string) => boolean;
  markUnavailable: (serviceName: string) => void;
}

// Configuration options for the API client wrapper
interface ApiClientWrapperOptions {
  serviceAvailability?: ServiceAvailabilityCallbacks;
}

export class ApiClientWrapper<T extends object> {
  private client: T;
  private serviceAvailability?: ServiceAvailabilityCallbacks;
  /** Tracks the last time a service-unavailable error was logged to Sentry per service */
  private static serviceUnavailableLogTimestamps: Record<string, number> = {};
  /** Tracks the last time a network-level error was logged to Sentry per method */
  private static networkErrorLogTimestamps: Record<string, number> = {};
  /** Tracks the last time a transient server error was logged to Sentry per method+status */
  private static transientErrorLogTimestamps: Record<string, number> = {};
  /**
   * Remembers which service (and tripping status) a method belongs to once it
   * has hit a 401/404. The URL is only known from the error response, so this
   * lets later calls fail fast during the service's cooldown instead of
   * re-hitting the endpoint (e.g. query keys that change on every render).
   */
  private static methodServiceMap: Record<
    string,
    { serviceName: string; status: number }
  > = {};
  private static SERVICE_LOG_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes

  constructor(client: T, options: ApiClientWrapperOptions = {}) {
    this.client = client;
    this.serviceAvailability = options.serviceAvailability;
  }

  /**
   * Create a proxied version of the client with error handling
   */
  getProxiedClient(): T {
    return new Proxy(this.client, {
      get: (target, prop: string | symbol) => {
        const property = prop as keyof T;
        const originalValue = target[property];

        if (typeof originalValue === 'function') {
          return async (...args: any[]) => {
            const trippedService =
              ApiClientWrapper.methodServiceMap[prop.toString()];
            if (
              trippedService &&
              this.serviceAvailability &&
              !this.serviceAvailability.isAvailable(trippedService.serviceName)
            ) {
              throw new ServiceUnavailableError(
                trippedService.serviceName,
                trippedService.status,
              );
            }

            requestRateMonitor.track('http');
            try {
              // Call the original method on the client
              return await (originalValue as Function).apply(target, args);
            } catch (error: any) {
              // Detect service unavailability from 404 (not found/deployed)
              // and 401 (caller unauthorized for this service) responses.
              // Without the 401 case, unauthorized users hit the same
              // endpoint on every page load, flooding Sentry with repeat
              // warnings instead of tripping the circuit breaker once.
              const status =
                error instanceof ResponseError
                  ? error.response?.status
                  : error.response?.status;
              const url = this.extractUrl(error);
              const serviceName = resolveServiceName(url);

              if (
                (status === 404 || status === 401) &&
                serviceName &&
                this.serviceAvailability
              ) {
                ApiClientWrapper.methodServiceMap[prop.toString()] = {
                  serviceName,
                  status,
                };
                this.serviceAvailability.markUnavailable(serviceName);
                this.logServiceUnavailableToSentry(
                  serviceName,
                  url,
                  prop.toString(),
                  status,
                );
                throw new ServiceUnavailableError(serviceName, status);
              }

              // Network-level failures never produce an HTTP response (status
              // is undefined). These are usually transient connectivity or
              // backend-availability issues rather than application bugs, and
              // React Query retries them — so logging once per attempt would
              // flood Sentry. Log them throttled at info level and surface the
              // error to the caller so the query still fails gracefully.
              if (this.isNetworkError(error, status)) {
                this.logNetworkErrorToSentry(error, prop.toString());
                const parsedNetworkError = await this.parseErrorResponse(error);
                throw parsedNetworkError;
              }

              // Transient gateway/proxy failures (502/503/504) are retried by
              // React Query and are not application bugs. Log them throttled at
              // warning level rather than flooding Sentry at error level.
              if (isTransientServerError(status)) {
                this.logTransientServerErrorToSentry(
                  error,
                  prop.toString(),
                  status as number,
                );
                const parsedTransientError =
                  await this.parseErrorResponse(error);
                throw parsedTransientError;
              }

              // Always log to Sentry
              this.logErrorToSentry(error, prop.toString(), args);

              // Apply centralized error handling
              const parsedError = await this.parseErrorResponse(error);
              throw parsedError;
            }
          };
        }

        // Return other properties as-is
        return originalValue;
      },
    }) as T;
  }

  /**
   * Log error to Sentry with context
   */
  private logErrorToSentry(error: any, methodName: string, args: any[]): void {
    try {
      // Extract relevant error information with defensive checks
      const status =
        error instanceof ResponseError
          ? error.response?.status
          : error.response?.status;

      const url = this.extractUrl(error);
      const errorMessage = error?.message || 'Unknown API Error';
      const endpoint = url ? this.formatUrlForDisplay(url) : 'unknown endpoint';

      // Create a more descriptive error for Sentry with method and endpoint details
      const sentryError = new Error(
        `API Error in ${methodName}() -> ${endpoint}: ${errorMessage}`,
      );
      sentryError.name = 'APIError';

      // Set Sentry context with additional details
      Sentry.withScope((scope) => {
        // Set error level based on status code
        if (status) {
          if (status >= 500) {
            scope.setLevel('error');
          } else if (status >= 400) {
            scope.setLevel('warning');
          } else {
            scope.setLevel('info');
          }
        }

        // Add tags for filtering
        scope.setTag('api.status', status || 'unknown');
        scope.setTag('api.method', methodName);
        if (url) {
          scope.setTag('api.endpoint', this.formatUrlForDisplay(url));
        }

        // Add context with more details
        scope.setContext('api_error', {
          status,
          url,
          method: methodName,
          timestamp: new Date().toISOString(),
          // Be careful not to log sensitive data in args
          argsCount: args?.length || 0,
        });

        // Add the original error as extra data with defensive checks
        const originalErrorContext: Record<string, any> = {};

        if (error?.message) {
          originalErrorContext.message = error.message;
        }

        if (error?.stack) {
          originalErrorContext.stack = error.stack;
        }

        if (error?.response) {
          originalErrorContext.response = {
            status: error.response?.status,
            statusText: error.response?.statusText,
            // Safely check for headers
            hasHeaders: !!error.response?.headers,
            // Avoid logging potentially sensitive response body
            hasBody: !!error.response?.body,
          };
        }

        // Only set context if we have data
        if (Object.keys(originalErrorContext).length > 0) {
          scope.setContext('original_error', originalErrorContext);
        }

        // Add breadcrumb for the API call
        Sentry.addBreadcrumb({
          category: 'api',
          message: `API call failed: ${methodName}`,
          level: status >= 500 ? 'error' : 'warning',
          data: {
            status,
            url,
            method: methodName,
          },
        });

        // Capture the error
        Sentry.captureException(sentryError);
      });
    } catch (sentryError) {
      // If Sentry logging fails, log to console but don't interrupt the flow
      console.error('Failed to log error to Sentry:', sentryError);
    }
  }

  /**
   * Log a service-unavailable event to Sentry, throttled to once per service
   * per cooldown window to avoid flooding.
   */
  private logServiceUnavailableToSentry(
    serviceName: string,
    url: string | undefined,
    methodName: string,
    status: number,
  ): void {
    try {
      const now = Date.now();
      const lastLogged =
        ApiClientWrapper.serviceUnavailableLogTimestamps[serviceName] ?? 0;

      if (now - lastLogged < ApiClientWrapper.SERVICE_LOG_COOLDOWN_MS) {
        return; // Already logged recently for this service
      }
      ApiClientWrapper.serviceUnavailableLogTimestamps[serviceName] = now;

      const endpoint = url ? this.formatUrlForDisplay(url) : 'unknown endpoint';
      const sentryError = new Error(
        `Service unavailable: "${serviceName}" (detected via ${methodName}() -> ${endpoint})`,
      );
      sentryError.name = 'ServiceUnavailable';

      Sentry.withScope((scope) => {
        scope.setLevel('info');
        scope.setTag('api.service_unavailable', 'true');
        scope.setTag('api.service_name', serviceName);
        scope.setTag('api.method', methodName);
        scope.setTag('api.status', String(status));
        if (url) {
          scope.setTag('api.endpoint', this.formatUrlForDisplay(url));
        }
        scope.setFingerprint(['service-unavailable', serviceName]);
        Sentry.captureException(sentryError);
      });
    } catch {
      // Don't interrupt the flow
    }
  }

  /**
   * Determine whether an error is a network-level failure, i.e. the request
   * never received an HTTP response. The generated API clients wrap fetch
   * rejections in a `FetchError`, and native `fetch` rejects with a
   * `TypeError` on network/CORS failures.
   */
  private isNetworkError(error: any, status: number | undefined): boolean {
    // Any error that carries an HTTP status/response is not network-level.
    if (status) return false;
    if (error?.response) return false;

    if (error?.name === 'FetchError') return true;
    if (error instanceof TypeError) return true;

    const message = typeof error?.message === 'string' ? error.message : '';
    return /failed to fetch|networkerror|network error|load failed|the request failed/i.test(
      message,
    );
  }

  /**
   * Log a network-level failure to Sentry, throttled to once per method per
   * cooldown window to avoid flooding when a backend is intermittently
   * unreachable (and React Query retries the request).
   */
  private logNetworkErrorToSentry(error: any, methodName: string): void {
    try {
      const now = Date.now();
      const lastLogged =
        ApiClientWrapper.networkErrorLogTimestamps[methodName] ?? 0;

      if (now - lastLogged < ApiClientWrapper.SERVICE_LOG_COOLDOWN_MS) {
        return; // Already logged recently for this method
      }
      ApiClientWrapper.networkErrorLogTimestamps[methodName] = now;

      const causeMessage =
        error?.cause?.message || error?.message || 'Network request failed';
      const sentryError = new Error(
        `Network error in ${methodName}(): ${causeMessage}`,
      );
      sentryError.name = 'APINetworkError';

      Sentry.withScope((scope) => {
        scope.setLevel('info');
        scope.setTag('api.network_error', 'true');
        scope.setTag('api.method', methodName);
        scope.setTag('api.status', 'network');
        scope.setFingerprint(['network-error', methodName]);
        Sentry.addBreadcrumb({
          category: 'api',
          message: `API network failure: ${methodName}`,
          level: 'info',
          data: { method: methodName },
        });
        Sentry.captureException(sentryError);
      });
    } catch {
      // Don't interrupt the flow
    }
  }

  /**
   * Log a transient gateway/proxy failure (502/503/504) to Sentry, throttled
   * to once per method+status per cooldown window. These are retried by React
   * Query, so logging every occurrence at error level would flood Sentry.
   */
  private logTransientServerErrorToSentry(
    error: any,
    methodName: string,
    status: number,
  ): void {
    try {
      const key = `${methodName}:${status}`;
      const now = Date.now();
      const lastLogged = ApiClientWrapper.transientErrorLogTimestamps[key] ?? 0;

      if (now - lastLogged < ApiClientWrapper.SERVICE_LOG_COOLDOWN_MS) {
        return; // Already logged recently for this method+status
      }
      ApiClientWrapper.transientErrorLogTimestamps[key] = now;

      const url = this.extractUrl(error);
      const endpoint = url ? this.formatUrlForDisplay(url) : 'unknown endpoint';
      const sentryError = new Error(
        `Transient server error (${status}) in ${methodName}() -> ${endpoint}`,
      );
      sentryError.name = 'APITransientServerError';

      Sentry.withScope((scope) => {
        scope.setLevel('warning');
        scope.setTag('api.transient_error', 'true');
        scope.setTag('api.method', methodName);
        scope.setTag('api.status', String(status));
        if (url) {
          scope.setTag('api.endpoint', this.formatUrlForDisplay(url));
        }
        scope.setFingerprint([
          'transient-server-error',
          methodName,
          String(status),
        ]);
        Sentry.addBreadcrumb({
          category: 'api',
          message: `API transient failure: ${methodName} (${status})`,
          level: 'warning',
          data: { method: methodName, status },
        });
        Sentry.captureException(sentryError);
      });
    } catch {
      // Don't interrupt the flow
    }
  }

  /**
   * Extract URL from error object
   */
  private extractUrl(error: any): string | undefined {
    // Try different common locations where the URL might be stored
    return (
      error.response?.url ||
      error.response?.config?.url ||
      error.config?.url ||
      error.url ||
      error.request?.responseURL ||
      error.request?.url
    );
  }

  /**
   * Format URL for display (optionally truncate or extract path)
   */
  private formatUrlForDisplay(url: string | undefined): string {
    if (!url) return '';

    try {
      // If it's a full URL, extract the path
      if (url.startsWith('http://') || url.startsWith('https://')) {
        const urlObj = new URL(url);
        // Return path + search params (without the domain)
        return urlObj.pathname + urlObj.search;
      }
      // If it's already a path, return as is
      return url;
    } catch {
      // If URL parsing fails, return the original
      return url;
    }
  }

  /**
   * Parse the error response and extract useful information.
   */
  private async parseErrorResponse(error: any): Promise<any> {
    if (error.response?.body) {
      try {
        // First, check if the response has a content-type header
        const contentType = error.response.headers?.get?.('content-type') || '';

        // If it's JSON, parse it
        if (contentType.includes('application/json')) {
          const errorBody = await error.response.json();
          error.response.message = errorBody?.message || 'An error occurred';
          error.response.parsedBody = errorBody;
        } else {
          // Otherwise, try to get it as text
          const textBody = await error.response.text();

          // Check if it's HTML and try to extract a meaningful message
          if (
            contentType.includes('text/html') ||
            textBody.trim().startsWith('<')
          ) {
            // Extract text from HTML error pages
            const match = textBody.match(/<h1>([^<]+)<\/h1>/);
            const htmlError = match ? match[1] : 'Server error';

            error.response.message = htmlError;
            error.response.parsedBody = {
              message: htmlError,
              originalHtml: textBody,
              isHtmlError: true,
            };
          } else {
            // Plain text response
            error.response.message = textBody || 'An error occurred';
            error.response.parsedBody = { message: textBody };
          }
        }

        return error;
      } catch (parsingError) {
        // If parsing fails, try to handle the error more gracefully
        try {
          // Attempt to read as text as a fallback
          const textBody = await error.response.text();
          error.response.message = textBody || 'Failed to parse error response';
          error.response.parsedBody = { message: textBody };
        } catch {
          // If even text parsing fails, return a generic message
          console.error('Failed to parse error response:', parsingError);
          error.response.message = 'Failed to parse error response';
        }

        return error;
      }
    }

    // If no response body exists, return the original error with additional info
    return error;
  }
}

// Hook-based wrapper factory for easy use with React hooks
export const useApiClientWrapper = () => {
  const serviceAvailability = useServiceAvailability();

  return <T extends object>(client: T): T => {
    const wrapper = new ApiClientWrapper(client, {
      serviceAvailability,
    });
    return wrapper.getProxiedClient();
  };
};
